import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "react-toastify";

import { useResolveNotificationMutation } from "@graphql/notifications/Notifications.g";

import { NotificationContextValue } from "./NotificationContext";

export const useNotificationProvider = (): NotificationContextValue => {
  const queryClient = useQueryClient();

  const resolveNotificationMutation = useResolveNotificationMutation();

  const invalidateNotificationQueries = useCallback(() => {
    // Invalidate all notification-related queries using the actual cache keys from GraphQL codegen
    // This will invalidate all queries that start with these prefixes, regardless of variables
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;
        return (
          Array.isArray(queryKey) &&
          (queryKey[0] === "Notifications" || 
           queryKey[0] === "UnresolvedNotificationsTotalCount" ||
           queryKey[0] === "ResourceGroupNotifications" ||
           queryKey[0] === "ClientApplicationTabsData" ||
           queryKey[0] === "ApplicationDashboardData")
        );
      },
    });
  }, [queryClient]);

  const resolveNotification = useCallback(
    async (id: string) => {
      try {
        await resolveNotificationMutation.mutateAsync({ input: { id } });
        toast.success("Notification cleared");
        invalidateNotificationQueries();
      } catch (error) {
        console.error("Failed to resolve notification:", error);
        toast.error("Failed to resolve notification");
      }
    },
    [resolveNotificationMutation, invalidateNotificationQueries],
  );

  return {
    resolveNotification,
    invalidateNotifications: invalidateNotificationQueries,
  };
};